import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from 'components/Common/ApiManagement/axiosInstance';
import { showToaster, handleErrors } from 'components/Common/common';
import {
    Shield, ChevronRight, Save, RefreshCw, Edit3, Plus, Trash2,
    Database, FileCode, Activity, Search, AlertCircle, CheckCircle,
    ChevronDown, ChevronUp, Settings, Layers, Code
} from 'lucide-react';
import './ComplianceAdmin.css';

const OPERATORS = [
    'equals', 'not_equals', 'contains', 'not_contains',
    'exists', 'not_exists', 'in', 'not_in',
    'greater_than', 'greater_than_or_equal',
    'less_than', 'less_than_or_equal',
    'starts_with', 'ends_with', 'regex'
];

const SCORING_METHODS = ['weighted_sum', 'weighted_average', 'all_pass', 'any_pass'];
const AGGREGATION_METHODS = ['all_rules_must_pass', 'weighted_average', 'any_rule_passes'];
const PRIORITIES = ['critical', 'high', 'medium', 'low'];

// Extract unique sorted scanner values from an eval rules data object
function extractScanners(evalData) {
    if (!evalData?.controls) return [];
    const controls = Array.isArray(evalData.controls)
        ? evalData.controls
        : Object.values(evalData.controls);
    const scanners = new Set();
    for (const ctrl of controls) {
        for (const rule of ctrl.evaluation_rules || []) {
            if (rule.scanner) scanners.add(rule.scanner);
        }
    }
    return [...scanners].sort();
}

// ─── Reusable Components ────────────────────────────────────────────────────

const Badge = ({ label, variant = 'default' }) => (
    <span className={`ca-badge ca-badge--${variant}`}>{label}</span>
);

const ExpandableSection = ({ title, icon, children, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="ca-expandable">
            <button className="ca-expandable__header" onClick={() => setOpen(o => !o)}>
                <span className="ca-expandable__title">{icon && <span className="ca-expandable__icon">{icon}</span>}{title}</span>
                {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {open && <div className="ca-expandable__body">{children}</div>}
        </div>
    );
};

// ─── Knowledge Base Control Editor ─────────────────────────────────────────

const KBControlEditor = ({ control, index, onChange }) => {
    const update = (field, value) => {
        const updated = { ...control };
        if (field.startsWith('actions.')) {
            const key = field.replace('actions.', '');
            updated.actions = { ...(updated.actions || {}), [key]: value };
        } else {
            updated[field] = value;
        }
        onChange(index, updated);
    };

    const updateRef = (refIdx, value) => {
        const refs = [...(control.actions?.references || [])];
        refs[refIdx] = value;
        update('actions.references', refs);
    };

    const addRef = () => update('actions.references', [...(control.actions?.references || []), '']);
    const removeRef = (i) => update('actions.references', (control.actions?.references || []).filter((_, idx) => idx !== i));

    return (
        <div className="ca-control-card">
            <div className="ca-control-card__header">
                <code className="ca-control-id">{control.control_id}</code>
                <span className="ca-control-name">{control.control_name}</span>
            </div>
            <div className="ca-form-grid">
                <div className="ca-form-group ca-form-group--full">
                    <label>Control Name</label>
                    <input className="ca-input" value={control.control_name || ''} onChange={e => update('control_name', e.target.value)} />
                </div>
                <div className="ca-form-group ca-form-group--full">
                    <label>Description</label>
                    <textarea className="ca-textarea" rows={2} value={control.description || ''} onChange={e => update('description', e.target.value)} />
                </div>
                <div className="ca-form-group ca-form-group--full">
                    <label>Requirement</label>
                    <input className="ca-input" value={control.requirement || ''} onChange={e => update('requirement', e.target.value)} />
                </div>

                {/* Actions sub-section */}
                <div className="ca-form-group ca-form-group--full">
                    <label>Remediation</label>
                    <textarea className="ca-textarea" rows={2} value={control.actions?.remediation || ''} onChange={e => update('actions.remediation', e.target.value)} />
                </div>
                <div className="ca-form-group">
                    <label>Timeline</label>
                    <input className="ca-input" placeholder="e.g. 30 days" value={control.actions?.timeline || ''} onChange={e => update('actions.timeline', e.target.value)} />
                </div>
                <div className="ca-form-group">
                    <label>CWE</label>
                    <input className="ca-input" placeholder="e.g. CWE-284" value={control.actions?.cwe || ''} onChange={e => update('actions.cwe', e.target.value)} />
                </div>

                {/* References */}
                <div className="ca-form-group ca-form-group--full">
                    <label>References</label>
                    <div className="ca-refs">
                        {(control.actions?.references || []).map((ref, i) => (
                            <div key={i} className="ca-ref-row">
                                <input className="ca-input" value={ref} onChange={e => updateRef(i, e.target.value)} placeholder="Reference URL or citation" />
                                <button className="ca-icon-btn ca-icon-btn--danger" onClick={() => removeRef(i)}><Trash2 size={14} /></button>
                            </div>
                        ))}
                        <button className="ca-btn ca-btn--ghost ca-btn--sm" onClick={addRef}><Plus size={14} /> Add Reference</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Eval Rule Condition Editor ─────────────────────────────────────────────

const ConditionEditor = ({ condition, onChange, onRemove }) => {
    const update = (key, value) => onChange({ ...condition, [key]: value });
    return (
        <div className="ca-condition">
            <div className="ca-condition__grid">
                <div>
                    <label>Field</label>
                    <input className="ca-input ca-input--sm" value={condition.field || ''} onChange={e => update('field', e.target.value)} placeholder="e.g. tls_1_2_supported" />
                </div>
                <div>
                    <label>Operator</label>
                    <select className="ca-select ca-select--sm" value={condition.operator || 'equals'} onChange={e => update('operator', e.target.value)}>
                        {OPERATORS.map(op => <option key={op} value={op}>{op}</option>)}
                    </select>
                </div>
                <div>
                    <label>Value</label>
                    <input className="ca-input ca-input--sm" value={
                        condition.value === null || condition.value === undefined ? '' :
                            typeof condition.value === 'object' ? JSON.stringify(condition.value) : String(condition.value)
                    } onChange={e => {
                        let v = e.target.value;
                        if (v === 'true') v = true;
                        else if (v === 'false') v = false;
                        else if (!isNaN(v) && v !== '') v = Number(v);
                        update('value', v);
                    }} placeholder="e.g. true, 100, 0" />
                </div>
                <div>
                    <label>Weight</label>
                    <input className="ca-input ca-input--sm" type="number" min={0} max={100} value={condition.weight ?? ''} onChange={e => update('weight', Number(e.target.value))} />
                </div>
                <div>
                    <label>Evidence Field</label>
                    <input className="ca-input ca-input--sm" value={condition.evidence_field || ''} onChange={e => update('evidence_field', e.target.value)} />
                </div>
                <div className="ca-condition__actions">
                    <button className="ca-icon-btn ca-icon-btn--danger" onClick={onRemove}><Trash2 size={14} /></button>
                </div>
            </div>
        </div>
    );
};

const EvalRuleEditor = ({ rule, ruleIndex, onChange, onRemove, availableScanners = [] }) => {
    // Make sure the current scanner is always a valid option even if not in the default known list
    const scannersToShow = [...new Set([...availableScanners, rule.scanner].filter(Boolean))];

    const update = (field, value) => {
        const updated = { ...rule };
        if (field.startsWith('scoring.')) {
            const key = field.replace('scoring.', '');
            if (key.startsWith('thresholds.')) {
                const tKey = key.replace('thresholds.', '');
                updated.scoring = { ...(updated.scoring || {}), thresholds: { ...(updated.scoring?.thresholds || {}), [tKey]: Number(value) } };
            } else {
                updated.scoring = { ...(updated.scoring || {}), [key]: value };
            }
        } else {
            updated[field] = value;
        }
        onChange(ruleIndex, updated);
    };

    const updateCondition = (condIdx, newCond) => {
        const conditions = [...(rule.conditions || [])];
        conditions[condIdx] = newCond;
        update('conditions', conditions);
    };

    const addCondition = () => {
        const conditions = [...(rule.conditions || []), { field: '', operator: 'equals', value: true, weight: 100, evidence_field: '' }];
        update('conditions', conditions);
    };

    const removeCondition = (condIdx) => {
        update('conditions', (rule.conditions || []).filter((_, i) => i !== condIdx));
    };

    return (
        <div className="ca-rule-editor">
            <div className="ca-rule-editor__header">
                <code className="ca-rule-id">{rule.rule_id || `Rule ${ruleIndex + 1}`}</code>
                <button className="ca-icon-btn ca-icon-btn--danger" onClick={() => onRemove(ruleIndex)} title="Remove rule"><Trash2 size={14} /></button>
            </div>
            <div className="ca-form-grid">
                <div className="ca-form-group">
                    <label>Rule ID</label>
                    <input className="ca-input" value={rule.rule_id || ''} onChange={e => update('rule_id', e.target.value)} />
                </div>
                <div className="ca-form-group">
                    <label>Scanner</label>
                    <select className="ca-select" value={rule.scanner || ''} onChange={e => update('scanner', e.target.value)}>
                        {scannersToShow.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div className="ca-form-group ca-form-group--full">
                    <label>Success Message</label>
                    <input className="ca-input" value={rule.success_message || ''} onChange={e => update('success_message', e.target.value)} />
                </div>
                <div className="ca-form-group ca-form-group--full">
                    <label>Failure Message</label>
                    <input className="ca-input" value={rule.failure_message || ''} onChange={e => update('failure_message', e.target.value)} />
                </div>

                {/* Scoring */}
                <div className="ca-form-group">
                    <label>Scoring Method</label>
                    <select className="ca-select" value={rule.scoring?.method || 'weighted_sum'} onChange={e => update('scoring.method', e.target.value)}>
                        {SCORING_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
                <div className="ca-form-group">
                    <label>Compliant Threshold</label>
                    <input className="ca-input" type="number" min={0} max={100} value={rule.scoring?.thresholds?.compliant ?? ''} onChange={e => update('scoring.thresholds.compliant', e.target.value)} />
                </div>
                <div className="ca-form-group">
                    <label>Partially Compliant Threshold</label>
                    <input className="ca-input" type="number" min={0} max={100} value={rule.scoring?.thresholds?.partially_compliant ?? ''} onChange={e => update('scoring.thresholds.partially_compliant', e.target.value)} />
                </div>
            </div>

            {/* Conditions */}
            <div className="ca-conditions-section">
                <div className="ca-conditions-header">
                    <span className="ca-conditions-label"><Activity size={14} /> Conditions ({(rule.conditions || []).length})</span>
                    <button className="ca-btn ca-btn--ghost ca-btn--sm" onClick={addCondition}><Plus size={14} /> Add Condition</button>
                </div>
                {(rule.conditions || []).map((cond, ci) => (
                    <ConditionEditor key={ci} condition={cond} onChange={(newCond) => updateCondition(ci, newCond)} onRemove={() => removeCondition(ci)} />
                ))}
                {(!rule.conditions || rule.conditions.length === 0) && (
                    <div className="ca-empty-msg">No conditions yet. Click "Add Condition" to begin.</div>
                )}
            </div>
        </div>
    );
};

// ─── Eval Rules Control Editor ──────────────────────────────────────────────

const EvalControlEditor = ({ control, controlKey, onChange, availableScanners }) => {
    const updateRule = (ruleIdx, updatedRule) => {
        const rules = [...(control.evaluation_rules || [])];
        rules[ruleIdx] = updatedRule;
        onChange(controlKey, { ...control, evaluation_rules: rules });
    };

    const addRule = () => {
        const newRule = {
            rule_id: `rule_${Date.now()}`,
            scanner: 'nuclei',
            conditions: [],
            scoring: { method: 'weighted_sum', thresholds: { compliant: 75, partially_compliant: 50 } },
            success_message: '',
            failure_message: ''
        };
        onChange(controlKey, { ...control, evaluation_rules: [...(control.evaluation_rules || []), newRule] });
    };

    const removeRule = (ruleIdx) => {
        onChange(controlKey, { ...control, evaluation_rules: (control.evaluation_rules || []).filter((_, i) => i !== ruleIdx) });
    };

    const updateAggregation = (key, value) => {
        onChange(controlKey, { ...control, aggregation: { ...(control.aggregation || {}), [key]: value === +value ? +value : value } });
    };

    const updateActions = (key, value) => {
        onChange(controlKey, { ...control, actions: { ...(control.actions || {}), [key]: value } });
    };

    return (
        <ExpandableSection
            title={<><code className="ca-control-id">{control.control_id}</code><span className="ca-control-name-sm">{control.control_name}</span></>}
            icon={<Shield size={14} />}
        >
            <div className="ca-form-grid ca-form-grid--tight">
                <div className="ca-form-group ca-form-group--full">
                    <label>Description</label>
                    <textarea className="ca-textarea" rows={2} value={control.description || ''} onChange={e => onChange(controlKey, { ...control, description: e.target.value })} />
                </div>
                <div className="ca-form-group ca-form-group--full">
                    <label>Requirement</label>
                    <input className="ca-input" value={control.requirement || ''} onChange={e => onChange(controlKey, { ...control, requirement: e.target.value })} />
                </div>

                {/* Aggregation */}
                <div className="ca-form-group">
                    <label>Aggregation Method</label>
                    <select className="ca-select" value={control.aggregation?.method || 'all_rules_must_pass'} onChange={e => updateAggregation('method', e.target.value)}>
                        {AGGREGATION_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
                <div className="ca-form-group">
                    <label>Minimum Score</label>
                    <input type="number" className="ca-input" min={0} max={100} value={control.aggregation?.minimum_score ?? ''} onChange={e => updateAggregation('minimum_score', e.target.value)} />
                </div>

                {/* Actions */}
                <div className="ca-form-group ca-form-group--full">
                    <label>Remediation</label>
                    <textarea className="ca-textarea" rows={2} value={control.actions?.remediation || ''} onChange={e => updateActions('remediation', e.target.value)} />
                </div>
                <div className="ca-form-group">
                    <label>Timeline</label>
                    <input className="ca-input" placeholder="e.g. 30 days" value={control.actions?.timeline || ''} onChange={e => updateActions('timeline', e.target.value)} />
                </div>
                <div className="ca-form-group">
                    <label>CWE</label>
                    <input className="ca-input" placeholder="e.g. CWE-284" value={control.actions?.cwe || ''} onChange={e => updateActions('cwe', e.target.value)} />
                </div>
                <div className="ca-form-group">
                    <label>Priority</label>
                    <select className="ca-select" value={control.actions?.priority || 'high'} onChange={e => updateActions('priority', e.target.value)}>
                        {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
                <div className="ca-form-group">
                    <label>Section</label>
                    <input className="ca-input" value={control.actions?.section || ''} onChange={e => updateActions('section', e.target.value)} />
                </div>

                {/* References */}
                <div className="ca-form-group ca-form-group--full">
                    <label>References</label>
                    <div className="ca-refs">
                        {(control.actions?.references || []).map((ref, i) => (
                            <div key={i} className="ca-ref-row">
                                <input className="ca-input" value={ref} onChange={e => {
                                    const refs = [...(control.actions?.references || [])];
                                    refs[i] = e.target.value;
                                    updateActions('references', refs);
                                }} placeholder="Reference URL or citation" />
                                <button className="ca-icon-btn ca-icon-btn--danger" onClick={() => {
                                    const refs = (control.actions?.references || []).filter((_, idx) => idx !== i);
                                    updateActions('references', refs);
                                }}><Trash2 size={14} /></button>
                            </div>
                        ))}
                        <button className="ca-btn ca-btn--ghost ca-btn--sm" onClick={() => {
                            updateActions('references', [...(control.actions?.references || []), '']);
                        }}><Plus size={14} /> Add Reference</button>
                    </div>
                </div>
            </div>

            {/* Evaluation Rules list */}
            <div className="ca-rules-section">
                <div className="ca-rules-header">
                    <span className="ca-section-title"><FileCode size={14} /> Evaluation Rules ({(control.evaluation_rules || []).length})</span>
                    <button className="ca-btn ca-btn--ghost ca-btn--sm" onClick={addRule}><Plus size={14} /> Add Rule</button>
                </div>
                {(control.evaluation_rules || []).map((rule, ri) => (
                    <EvalRuleEditor key={ri} rule={rule} ruleIndex={ri} onChange={updateRule} onRemove={removeRule} availableScanners={availableScanners} />
                ))}
                {(!control.evaluation_rules || control.evaluation_rules.length === 0) && (
                    <div className="ca-empty-msg">No evaluation rules. Click "Add Rule" to create one.</div>
                )}
            </div>
        </ExpandableSection>
    );
};

// ─── Main Page ──────────────────────────────────────────────────────────────

const ComplianceAdmin = () => {
    const [frameworks, setFrameworks] = useState([]);
    const [selectedFramework, setSelectedFramework] = useState(null);
    const [activeTab, setActiveTab] = useState('eval'); // 'kb' | 'eval'
    const [kbData, setKbData] = useState(null);
    const [evalData, setEvalData] = useState(null);
    const [kbControls, setKbControls] = useState([]);
    const [evalControls, setEvalControls] = useState(null); // object or array from JSON
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [frameworkMeta, setFrameworkMeta] = useState({});

    // ── Load framework list ────────────────────────────────────────────────
    useEffect(() => {
        axiosInstance.get('/compliance-admin/frameworks')
            .then(res => setFrameworks(res.data.data || []))
            .catch(err => showToaster.error('Failed to load frameworks: ' + (err.response?.data?.error || err.message)));
    }, []);

    // ── Load KB + Eval Rules when framework changes ────────────────────────
    useEffect(() => {
        if (!selectedFramework) return;
        loadFrameworkData(selectedFramework);
    }, [selectedFramework]);

    const loadFrameworkData = async (fwId) => {
        setLoading(true);
        setKbData(null);
        setEvalData(null);
        try {
            const [kbRes, evalRes] = await Promise.allSettled([
                axiosInstance.get(`/compliance-admin/knowledge-base/${encodeURIComponent(fwId)}`),
                axiosInstance.get(`/compliance-admin/eval-rules/${encodeURIComponent(fwId)}`),
            ]);

            if (kbRes.status === 'fulfilled') {
                const kb = kbRes.value.data.data;
                setKbData(kb);
                setKbControls(Array.isArray(kb.controls) ? kb.controls : Object.values(kb.controls || {}));
            }
            if (evalRes.status === 'fulfilled') {
                const ev = evalRes.value.data.data;
                setEvalData(ev);
                setEvalControls(ev.controls); // preserve as-is (object or array)
                setFrameworkMeta({ framework_id: ev.framework_id, framework_name: ev.framework_name, version: ev.version, description: ev.description, metadata: ev.metadata });
            }
        } catch (err) {
            showToaster.error('Error loading framework data');
        } finally {
            setLoading(false);
        }
    };

    // ── Save KB ────────────────────────────────────────────────────────────
    const saveKB = async () => {
        if (!selectedFramework || !kbData) return;
        setSaving(true);
        try {
            await axiosInstance.put(`/compliance-admin/knowledge-base/${encodeURIComponent(selectedFramework)}`, {
                ...kbData,
                controls: kbControls,
            });
            showToaster(1, 'Knowledge Base saved successfully');
        } catch (err) {
            handleErrors(err);
        } finally {
            setSaving(false);
        }
    };

    // ── Save Eval Rules ────────────────────────────────────────────────────
    const saveEvalRules = async () => {
        if (!selectedFramework || !evalData) return;
        setSaving(true);
        try {
            await axiosInstance.put(`/compliance-admin/eval-rules/${encodeURIComponent(selectedFramework)}`, {
                ...evalData,
                controls: evalControls,
            });
            showToaster(1, 'Evaluation Rules saved successfully');
        } catch (err) {
            handleErrors(err);
        } finally {
            setSaving(false);
        }
    };

    // ── KB control change ──────────────────────────────────────────────────
    const handleKbControlChange = useCallback((index, updated) => {
        setKbControls(prev => prev.map((c, i) => i === index ? updated : c));
    }, []);

    // ── Eval Rules control change ──────────────────────────────────────────
    const handleEvalControlChange = useCallback((key, updated) => {
        setEvalControls(prev => {
            if (Array.isArray(prev)) {
                const newArr = [...prev];
                const idx = newArr.findIndex(c => c.control_id === key || c.control_id === updated.control_id);
                if (idx !== -1) newArr[idx] = updated;
                return newArr;
            } else {
                return { ...prev, [key]: updated };
            }
        });
    }, []);

    // ── Filtered frameworks ────────────────────────────────────────────────
    const filteredFrameworks = frameworks.filter(f =>
        (f.frameworkName || f.framework_name || '')?.toLowerCase().includes(search.toLowerCase()) ||
        f.framework_id?.toLowerCase().includes(search.toLowerCase())
    );

    // ── Get display controls for eval tab ─────────────────────────────────
    const evalControlsArray = evalControls
        ? (Array.isArray(evalControls) ? evalControls : Object.values(evalControls))
        : [];

    const evalControlsMap = evalControls && !Array.isArray(evalControls)
        ? evalControls
        : (evalControls || []).reduce((acc, c) => { acc[c.control_id] = c; return acc; }, {});

    return (
        <div className="ca-page">
            {/* ── Header ─────────────────────────────────────────────────────── */}
            <div className="ca-header">
                <div className="ca-header__title">
                    <Shield className="ca-header__icon" size={28} />
                    <div>
                        <h1>Compliance Rules Admin</h1>
                        <p>Manage knowledge base and evaluation rules for all compliance frameworks</p>
                    </div>
                </div>
                <div className="ca-header__stats">
                    <div className="ca-stat"><Database size={16} /><span>{frameworks.length}</span><label>Frameworks</label></div>
                </div>
            </div>

            <div className="ca-layout">
                {/* ── Left Sidebar: Framework list ───────────────────────────── */}
                <aside className="ca-sidebar">
                    <div className="ca-sidebar__search">
                        <Search size={16} className="ca-sidebar__search-icon" />
                        <input className="ca-sidebar__search-input" placeholder="Search frameworks..." value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                    <div className="ca-sidebar__list">
                        {filteredFrameworks.map(fw => (
                            <button
                                key={fw.framework_id}
                                className={`ca-fw-item ${selectedFramework === fw.framework_id ? 'ca-fw-item--active' : ''}`}
                                onClick={() => setSelectedFramework(fw.framework_id)}
                            >
                                <div className="ca-fw-item__content">
                                    <span className="ca-fw-item__id">{fw.framework_id}</span>
                                    <span className="ca-fw-item__name">{fw.frameworkName || fw.framework_name}</span>
                                    {fw.source === 'eval_only' && <Badge label="Rules Only" variant="warning" />}
                                </div>
                                <ChevronRight size={14} className="ca-fw-item__arrow" />
                            </button>
                        ))}
                        {filteredFrameworks.length === 0 && (
                            <div className="ca-sidebar__empty">No frameworks found</div>
                        )}
                    </div>
                </aside>

                {/* ── Right Panel ────────────────────────────────────────────── */}
                <main className="ca-main">
                    {!selectedFramework ? (
                        <div className="ca-welcome">
                            <Settings size={56} strokeWidth={1} />
                            <h2>Select a Framework</h2>
                            <p>Choose a compliance framework from the list to view and edit its knowledge base and evaluation rules.</p>
                        </div>
                    ) : loading ? (
                        <div className="ca-loading">
                            <RefreshCw size={32} className="ca-loading__icon" />
                            <p>Loading framework data...</p>
                        </div>
                    ) : (
                        <>
                            {/* Framework Title + Save */}
                            <div className="ca-panel-header">
                                <div>
                                    <h2 className="ca-panel-title">
                                        {frameworks.find(f => f.framework_id === selectedFramework)?.frameworkName
                                            || frameworks.find(f => f.framework_id === selectedFramework)?.framework_name
                                            || selectedFramework}
                                    </h2>
                                    <code className="ca-panel-id">{selectedFramework}</code>
                                </div>
                                <div className="ca-panel-actions">
                                    <button className="ca-btn ca-btn--ghost" onClick={() => loadFrameworkData(selectedFramework)}>
                                        <RefreshCw size={14} /> Reload
                                    </button>
                                    <button
                                        className="ca-btn ca-btn--primary"
                                        disabled={saving}
                                        onClick={activeTab === 'kb' ? saveKB : saveEvalRules}
                                    >
                                        <Save size={14} />
                                        {saving ? 'Saving...' : (activeTab === 'kb' ? 'Save Knowledge Base' : 'Save Eval Rules')}
                                    </button>
                                </div>
                            </div>

                            {/* Tabs */}
                            <div className="ca-tabs">
                                {/* <button className={`ca-tab ${activeTab === 'kb' ? 'ca-tab--active' : ''}`} onClick={() => setActiveTab('kb')}>
                                    <Database size={15} /> Knowledge Base {kbControls.length > 0 && <Badge label={kbControls.length} variant="info" />}
                                </button> */}
                                <button className={`ca-tab ${activeTab === 'eval' ? 'ca-tab--active' : ''}`} onClick={() => setActiveTab('eval')}>
                                    <Code size={15} /> Evaluation Rules {evalControlsArray.length > 0 && <Badge label={evalControlsArray.length} variant="info" />}
                                </button>
                            </div>

                            {/* ── KB Tab ────────────────────────────────────────────── */}
                            {activeTab === 'kb' && (
                                <div className="ca-tab-content">
                                    {!kbData ? (
                                        <div className="ca-not-found">
                                            <AlertCircle size={32} />
                                            <p>No knowledge base data found for this framework in MongoDB.</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Framework-level fields */}
                                            <div className="ca-section-card">
                                                <h3 className="ca-section-title"><Layers size={16} /> Framework Info</h3>
                                                <div className="ca-form-grid">
                                                    <div className="ca-form-group">
                                                        <label>Framework Name</label>
                                                        <input className="ca-input" value={kbData.frameworkName || kbData.framework_name || ''} onChange={e => setKbData(d => ({ ...d, frameworkName: e.target.value }))} />
                                                    </div>
                                                    <div className="ca-form-group">
                                                        <label>Category</label>
                                                        <input className="ca-input" value={kbData.category || kbData.framework_category || ''} onChange={e => setKbData(d => ({ ...d, framework_category: e.target.value, category: e.target.value }))} />
                                                    </div>
                                                    <div className="ca-form-group">
                                                        <label>Regulatory Authority</label>
                                                        <input className="ca-input" value={kbData.regulatoryAuthority || ''} onChange={e => setKbData(d => ({ ...d, regulatoryAuthority: e.target.value }))} />
                                                    </div>
                                                    <div className="ca-form-group">
                                                        <label>Version</label>
                                                        <input className="ca-input" value={kbData.version || ''} onChange={e => setKbData(d => ({ ...d, version: e.target.value }))} />
                                                    </div>
                                                    <div className="ca-form-group ca-form-group--full">
                                                        <label>Description</label>
                                                        <textarea className="ca-textarea" rows={2} value={kbData.description || ''} onChange={e => setKbData(d => ({ ...d, description: e.target.value }))} />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Controls */}
                                            <div className="ca-section-card">
                                                <h3 className="ca-section-title"><Shield size={16} /> Controls ({kbControls.length})</h3>
                                                <div className="ca-controls-list">
                                                    {kbControls.map((ctrl, i) => (
                                                        <KBControlEditor key={ctrl.control_id || i} control={ctrl} index={i} onChange={handleKbControlChange} />
                                                    ))}
                                                    {kbControls.length === 0 && <div className="ca-empty-msg">No controls found in knowledge base.</div>}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* ── Eval Rules Tab ────────────────────────────────────── */}
                            {activeTab === 'eval' && (
                                <div className="ca-tab-content">
                                    {!evalData ? (
                                        <div className="ca-not-found">
                                            <AlertCircle size={32} />
                                            <p>No evaluation rules file found for this framework.</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Framework-level fields */}
                                            <div className="ca-section-card">
                                                <h3 className="ca-section-title"><FileCode size={16} /> Rules File Info</h3>
                                                <div className="ca-form-grid">
                                                    <div className="ca-form-group ca-form-group--full">
                                                        <label>Description</label>
                                                        <textarea className="ca-textarea" rows={2} value={evalData.description || ''} onChange={e => setEvalData(d => ({ ...d, description: e.target.value }))} />
                                                    </div>
                                                    <div className="ca-form-group">
                                                        <label>Version</label>
                                                        <input className="ca-input" value={evalData.version || ''} onChange={e => setEvalData(d => ({ ...d, version: e.target.value }))} />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Controls with Eval Rules */}
                                            <div className="ca-section-card">
                                                <h3 className="ca-section-title"><Activity size={16} /> Controls & Evaluation Rules ({evalControlsArray.length})</h3>
                                                <div className="ca-controls-list">
                                                    {evalControlsArray.map((ctrl, i) => (
                                                        <EvalControlEditor
                                                            key={ctrl.control_id || i}
                                                            control={ctrl}
                                                            controlKey={Array.isArray(evalControls) ? ctrl.control_id : ctrl.control_id}
                                                            onChange={handleEvalControlChange}
                                                        />
                                                    ))}
                                                    {evalControlsArray.length === 0 && <div className="ca-empty-msg">No controls with evaluation rules found.</div>}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};

export default ComplianceAdmin;
